'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { PlusIcon, MessageSquareIcon, Trash2Icon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface Thread {
  id: string;
  title: string;
  updatedAt?: Date;
  updatedAtZ?: string;
  createdAt?: Date;
  createdAtZ?: string;
  messageCount?: number;
}

interface MomentoThreadListProps {
  userId: string;
  domainId: string;
  activeThreadId: string;
  onThreadSelect: (threadId: string) => void;
  onNewThread: () => void;
  onRefreshCallbackSet?: (refreshFn: () => void) => void;
}

export function MomentoThreadList({
  userId,
  domainId,
  activeThreadId,
  onThreadSelect,
  onNewThread,
  onRefreshCallbackSet,
}: MomentoThreadListProps) {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchThreads();
  }, [userId, domainId]);

  // Expose fetchThreads to parent via callback
  useEffect(() => {
    if (onRefreshCallbackSet) {
      onRefreshCallbackSet(fetchThreads);
    }
  }, [onRefreshCallbackSet]);

  const fetchThreads = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/threads?userId=${userId}&domainId=${domainId}`);
      if (!res.ok) {
        throw new Error('Failed to fetch threads');
      }
      const data = await res.json();
      setThreads(data.threads || []);
    } catch (error) {
      console.error('[ThreadList] Failed to fetch threads:', error);
      setThreads([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteThread = async (threadId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent thread selection

    if (!confirm('Delete this conversation? This cannot be undone.')) {
      return;
    }

    try {
      const res = await fetch(`/api/threads/${threadId}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        throw new Error('Failed to delete thread');
      }

      // Refresh thread list to remove deleted thread
      await fetchThreads();

      // If deleted thread was active, create new thread
      if (threadId === activeThreadId) {
        onNewThread();
      }
    } catch (error) {
      console.error('[ThreadList] Failed to delete thread:', error);
      alert('Failed to delete conversation');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full border-r bg-muted/10 w-64">
        <div className="p-4 border-b">
          <Button onClick={onNewThread} className="w-full" size="sm" disabled>
            <PlusIcon className="w-4 h-4 mr-2" />
            New Thread
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-sm text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full border-r bg-muted/10 w-64">
      {/* Header */}
      <div className="p-4 border-b">
        <Button onClick={onNewThread} className="w-full" size="sm">
          <PlusIcon className="w-4 h-4 mr-2" />
          New Thread
        </Button>
      </div>

      {/* Thread List */}
      <div className="flex-1 overflow-y-auto">
        {threads.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            No conversations yet.
            <br />
            Click "New Thread" to start.
          </div>
        ) : (
          <div className="divide-y">
            {threads.map((thread) => (
              <button
                key={thread.id}
                onClick={() => onThreadSelect(thread.id)}
                className={`
                  w-full p-3 text-left hover:bg-accent transition-colors
                  ${activeThreadId === thread.id ? 'bg-accent border-l-2 border-primary' : ''}
                  group relative
                `}
              >
                <div className="flex items-start gap-2">
                  <MessageSquareIcon className="w-4 h-4 mt-1 flex-shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0 pr-6">
                    <p className="font-medium truncate text-sm">
                      {thread.title || 'Untitled Thread'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {thread.updatedAtZ || thread.updatedAt || thread.createdAtZ || thread.createdAt
                        ? formatDistanceToNow(new Date(thread.updatedAtZ || thread.updatedAt || thread.createdAtZ || thread.createdAt!), { addSuffix: true })
                        : 'Just now'}
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleDeleteThread(thread.id, e)}
                    className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-destructive/10 rounded"
                    aria-label="Delete thread"
                  >
                    <Trash2Icon className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer info */}
      <div className="p-3 border-t text-xs text-muted-foreground text-center">
        {threads.length} {threads.length === 1 ? 'conversation' : 'conversations'}
      </div>
    </div>
  );
}
